import { useState } from 'react';
import { useLosses } from '../../hooks/useData';
import { api, getCurrentMonth, formatCurrency, LOSS_REASONS } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Trash2, Plus, X, AlertTriangle, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDelete from '../../components/ConfirmDelete';

const PatronPertes = () => {
  const currentMonth = getCurrentMonth();
  const today = new Date().toISOString().split('T')[0];
  const { losses, loading, refetch } = useLosses(currentMonth);

  const [showForm, setShowForm] = useState(false);
  const [editingLoss, setEditingLoss] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({ produit: '', quantite: '', unite: 'kg', prix_unitaire: '', raison: 'Autre', date: today });

  const submit = async () => {
    if (!form.produit.trim()) { toast.error('Nom du produit requis'); return; }
    if (!form.quantite || parseFloat(form.quantite) <= 0) { toast.error('Quantité requise'); return; }
    try {
      await api.post('/losses', {
        date: form.date,
        produit: form.produit,
        quantite: parseFloat(form.quantite),
        unite: form.unite,
        prix_unitaire: parseFloat(form.prix_unitaire) || 0,
        raison: form.raison,
      });
      const montant = (parseFloat(form.quantite) || 0) * (parseFloat(form.prix_unitaire) || 0);
      toast.success(`${form.produit} — ${formatCurrency(montant)}`);
      setForm({ produit: '', quantite: '', unite: 'kg', prix_unitaire: '', raison: 'Autre', date: today });
      setShowForm(false);
      refetch();
    } catch { toast.error('Erreur'); }
  };

  const deleteLoss = async (id) => {
    try {
      await api.delete(`/losses/${id}`);
      toast.success('Supprimée');
      refetch();
    } catch { toast.error('Erreur'); }
  };

  const startEditLoss = (loss) => {
    setEditingLoss(loss.id);
    setEditForm({
      produit: loss.produit,
      quantite: loss.quantite,
      unite: loss.unite,
      prix_unitaire: loss.prix_unitaire,
      raison: loss.raison || '',
    });
  };

  const saveEditLoss = async () => {
    try {
      await api.put(`/losses/${editingLoss}`, {
        produit: editForm.produit,
        quantite: parseFloat(editForm.quantite) || 0,
        unite: editForm.unite,
        prix_unitaire: parseFloat(editForm.prix_unitaire) || 0,
        raison: editForm.raison,
      });
      toast.success('Perte modifiée');
      setEditingLoss(null);
      refetch();
    } catch { toast.error('Erreur'); }
  };

  const todayLosses = losses.filter(l => l.date === today);
  const todayTotal = todayLosses.reduce((s, l) => s + (l.montant || 0), 0);
  const monthTotal = losses.reduce((s, l) => s + (l.montant || 0), 0);

  // Group by date
  const byDate = {};
  losses.filter(l => l.date !== today).forEach(l => {
    if (!byDate[l.date]) byDate[l.date] = [];
    byDate[l.date].push(l);
  });

  return (
    <div className="space-y-4 p-4 max-w-4xl mx-auto" data-testid="patron-pertes">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
            <Trash2 size={20} className="text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-title text-[rgb(var(--lm-text))]">Pertes</h1>
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Suivi et saisie</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(!showForm)}
          className={showForm ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'bg-red-500 text-white hover:bg-red-600'}
          data-testid="patron-add-perte-btn">
          {showForm ? <X size={16} /> : <><Plus size={16} className="mr-1" /> Saisir une perte</>}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-3 border border-[rgb(var(--lm-border)/0.3)]">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Aujourd'hui</p>
          <p className="text-xl font-bold font-mono mt-1 text-red-400">{formatCurrency(todayTotal)}</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{todayLosses.length} perte(s)</p>
        </div>
        <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-3 border border-[rgb(var(--lm-border)/0.3)]">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Ce mois</p>
          <p className="text-xl font-bold font-mono mt-1 text-[rgb(var(--lm-text))]">{formatCurrency(monthTotal)}</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{losses.length} perte(s)</p>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[rgb(var(--lm-surface))] rounded-2xl border border-red-500/20 p-4 space-y-3 animate-slide-up" data-testid="patron-perte-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Produit *</label>
              <Input value={form.produit} onChange={e => setForm(f => ({ ...f, produit: e.target.value }))}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Ex: Filet de bar" autoFocus data-testid="patron-perte-produit" />
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Date</label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm" data-testid="patron-perte-date" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Quantité *</label>
              <Input type="number" step="0.1" value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0" data-testid="patron-perte-qte" />
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Unité</label>
              <select value={form.unite} onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}
                className="w-full h-10 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-sm px-3">
                <option value="kg">kg</option><option value="piece">pièce</option><option value="L">L</option><option value="barq">barq</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Prix/unité (€)</label>
              <Input type="number" step="0.01" value={form.prix_unitaire} onChange={e => setForm(f => ({ ...f, prix_unitaire: e.target.value }))}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0.00" data-testid="patron-perte-prix" />
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Raison</label>
              <select value={form.raison} onChange={e => setForm(f => ({ ...f, raison: e.target.value }))}
                className="w-full h-10 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-sm px-3"
                data-testid="patron-perte-raison">
                {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {(parseFloat(form.quantite) > 0 && parseFloat(form.prix_unitaire) > 0) && (
            <p className="text-right text-sm font-mono text-red-400">
              = {formatCurrency((parseFloat(form.quantite) || 0) * (parseFloat(form.prix_unitaire) || 0))}
            </p>
          )}
          <Button onClick={submit} className="w-full bg-red-500 text-white hover:bg-red-600" data-testid="patron-submit-perte">
            <AlertTriangle size={16} className="mr-2" /> Enregistrer la perte
          </Button>
        </div>
      )}

      {/* Today's losses */}
      {todayLosses.length > 0 && (
        <div className="bg-[rgb(var(--lm-surface))] rounded-xl border border-[rgb(var(--lm-border)/0.3)] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[rgb(var(--lm-border)/0.3)]">
            <h3 className="text-xs font-medium text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Aujourd'hui</h3>
          </div>
          <div className="divide-y divide-[rgb(var(--lm-border)/0.2)]">
            {todayLosses.map(l => (
              editingLoss === l.id ? (
                <div key={l.id} className="p-3 bg-[rgb(var(--lm-bg))] space-y-2" data-testid={`edit-loss-${l.id}`}>
                  <Input value={editForm.produit} onChange={e => setEditForm(f => ({ ...f, produit: e.target.value }))}
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" />
                  <div className="flex gap-2 flex-wrap">
                    <Input type="number" value={editForm.quantite} onChange={e => setEditForm(f => ({ ...f, quantite: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-sm w-20" placeholder="Qté" />
                    <select value={editForm.unite} onChange={e => setEditForm(f => ({ ...f, unite: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-sm text-[rgb(var(--lm-text))] w-20">
                      <option value="kg">kg</option><option value="piece">pièce</option><option value="L">L</option><option value="barq">barq</option>
                    </select>
                    <Input type="number" step="0.01" value={editForm.prix_unitaire} onChange={e => setEditForm(f => ({ ...f, prix_unitaire: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-sm w-24" placeholder="€/u" />
                    <select value={editForm.raison} onChange={e => setEditForm(f => ({ ...f, raison: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-sm text-[rgb(var(--lm-text))] flex-1 min-w-[100px]">
                      <option value="">Raison...</option>
                      {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditingLoss(null)} className="text-[rgb(var(--lm-text-muted))]"><X size={14} className="mr-1" /> Annuler</Button>
                    <Button size="sm" onClick={saveEditLoss} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]"><Check size={14} className="mr-1" /> OK</Button>
                  </div>
                </div>
              ) : (
                <div key={l.id} className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-[rgb(var(--lm-surface-elevated)/0.3)] transition-colors"
                  onClick={() => startEditLoss(l)} data-testid={`loss-${l.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{l.produit}</p>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{l.quantite} {l.unite} x {l.prix_unitaire}€ — {l.raison}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-red-400">{formatCurrency(l.montant)}</span>
                    <Pencil size={11} className="text-[rgb(var(--lm-text-muted))]" />
                    <ConfirmDelete onConfirm={() => deleteLoss(l.id)}>
                      <Button size="sm" variant="ghost" className="text-red-400/40 hover:text-red-400 h-7 px-1.5"><Trash2 size={13} /></Button>
                    </ConfirmDelete>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* History by date */}
      {Object.keys(byDate).length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-medium text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Historique</h3>
          {Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0])).map(([date, items]) => {
            const total = items.reduce((s, l) => s + (l.montant || 0), 0);
            return (
              <div key={date} className="bg-[rgb(var(--lm-surface))] rounded-xl border border-[rgb(var(--lm-border)/0.3)] overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b border-[rgb(var(--lm-border)/0.2)]">
                  <span className="text-xs font-mono text-[rgb(var(--lm-text-muted))]">{date}</span>
                  <span className="text-xs font-mono text-red-400">{formatCurrency(total)}</span>
                </div>
                <div className="divide-y divide-[rgb(var(--lm-border)/0.1)]">
                  {items.map(l => (
                    editingLoss === l.id ? (
                      <div key={l.id} className="p-3 bg-[rgb(var(--lm-bg))] space-y-2">
                        <Input value={editForm.produit} onChange={e => setEditForm(f => ({ ...f, produit: e.target.value }))}
                          className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" />
                        <div className="flex gap-2 flex-wrap">
                          <Input type="number" value={editForm.quantite} onChange={e => setEditForm(f => ({ ...f, quantite: e.target.value }))}
                            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-sm w-20" />
                          <Input type="number" step="0.01" value={editForm.prix_unitaire} onChange={e => setEditForm(f => ({ ...f, prix_unitaire: e.target.value }))}
                            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-sm w-24" />
                          <select value={editForm.raison} onChange={e => setEditForm(f => ({ ...f, raison: e.target.value }))}
                            className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-sm text-[rgb(var(--lm-text))] flex-1">
                            {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => setEditingLoss(null)}><X size={14} /></Button>
                          <Button size="sm" onClick={saveEditLoss} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]"><Check size={14} className="mr-1" /> OK</Button>
                        </div>
                      </div>
                    ) : (
                      <div key={l.id} className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[rgb(var(--lm-surface-elevated)/0.3)] transition-colors"
                        onClick={() => startEditLoss(l)}>
                        <div>
                          <p className="text-sm text-[rgb(var(--lm-text))]">{l.produit}</p>
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{l.quantite} {l.unite} x {l.prix_unitaire}€ — {l.raison}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-red-400">{formatCurrency(l.montant)}</span>
                          <Pencil size={10} className="text-[rgb(var(--lm-text-muted))]" />
                          <ConfirmDelete onConfirm={() => deleteLoss(l.id)}>
                            <Button size="sm" variant="ghost" className="text-red-400/30 hover:text-red-400 h-6 px-1"><Trash2 size={12} /></Button>
                          </ConfirmDelete>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {losses.length === 0 && !loading && (
        <div className="text-center py-12">
          <Trash2 size={40} className="mx-auto text-[rgb(var(--lm-border))] mb-3" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune perte ce mois</p>
        </div>
      )}
    </div>
  );
};

export default PatronPertes;
